import React, { useCallback, useMemo, useState } from 'react'
import cx from 'classnames'
import { BtnV2, l } from '@manychat/manyui'

import { useAppDispatch } from 'reduxTyped'
import { ReadMode } from 'apps/aiAutomation/components/KnowledgeEditor/components/ReadMode'
import { useSources } from 'apps/aiAutomation/components/KnowledgeEditor/hooks/useSources'
import { KnowledgeEditorContext } from 'apps/aiAutomation/components/KnowledgeEditor/lib/context'
import { SourceChunk } from 'apps/aiAutomation/components/Playground/types'
import { SidebarHeader } from 'apps/aiAutomation/components/SidebarHeader'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import {
  KnowledgeEntry,
  KnowledgeEntryDraft,
  KnowledgeEntryType,
  LinkEntry,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'

import { LinkWithPreviewEditor } from './components/LinkWithPreviewEditor'
import { TextEntryEditor } from './components/TextEntryEditor'
import { useEditor } from './hooks/useEditor'
import { getSidebarTitle } from './lib/getSidebarTitle'
import { KnowledgeEditorSource } from './lib/types'

import cm from './styles.module.css'

interface KnowledgeEditorProps {
  entryId?: string | null
  newEntryType?: KnowledgeEntryType
  editMode?: boolean
  onSave?: (entry: KnowledgeEntry | KnowledgeEntryDraft, isNew: boolean) => void
  onClose: () => void
  source: KnowledgeEditorSource
  highlightedChunks?: SourceChunk[]
}

export const KnowledgeEditor = ({
  source,
  entryId,
  newEntryType,
  editMode,
  onSave,
  onClose,
  highlightedChunks = [],
}: KnowledgeEditorProps) => {
  const dispatch = useAppDispatch()

  const [isEditModeEnabled, setIsEditModeEnabled] = useState(Boolean(newEntryType) || editMode)

  const { isLoading, selectedEntry, save } = useEditor({
    setIsEditModeEnabled,
    entryId,
    onSave,
    source,
  })

  const sourcesData = useSources({ highlightedChunks, entryId, selectedEntry })

  const contextValue = useMemo(
    () => ({ onClose, source, ...sourcesData }),
    [onClose, source, sourcesData],
  )

  const handleBackToReadMode = useCallback(async () => {
    const confirmed = await dispatch(
      AiAutomationLayoutActions.actionRequestedWithConfirmation(),
    ).unwrap()

    if (confirmed) {
      setIsEditModeEnabled(false)
    }
  }, [dispatch])

  const entryType = selectedEntry?.type || newEntryType || KnowledgeEntryType.LINK

  const isReadMode = !isEditModeEnabled && selectedEntry && !isLoading

  return (
    <KnowledgeEditorContext.Provider value={contextValue}>
      <section className={cm.knowledgeEditor}>
        {source === KnowledgeEditorSource.SIDEBAR && (
          <SidebarHeader
            title={getSidebarTitle({ entry: selectedEntry, newEntryType: entryType })}
            button={
              !isEditModeEnabled && (
                <BtnV2
                  label={l.translate('Edit')}
                  ariaLabel={l.translate('Edit')}
                  leftIcon="Edit"
                  size="small"
                  data-test-id="ai-automation/knowledge-editor-switch-to-edit-mode"
                  onClick={() => setIsEditModeEnabled(true)}
                />
              )
            }
          />
        )}

        {source === KnowledgeEditorSource.PLAYGROUND && (
          <div className={cm.knowledgeEditor__playgroundHeader}>
            {!isEditModeEnabled && (
              <BtnV2
                ariaLabel={l.translate('Back to playground')}
                onlyIcon="DoubleChevrotBack"
                size="small"
                onClick={onClose}
              />
            )}

            <h2 className="m-t-0">
              {getSidebarTitle({ entry: selectedEntry, newEntryType: entryType })}
            </h2>
            {!isEditModeEnabled && (
              <BtnV2
                label={l.translate('Edit')}
                ariaLabel={l.translate('Edit')}
                className="m-l-auto"
                leftIcon="Edit"
                size="small"
                data-test-id="ai-automation/knowledge-editor-switch-to-edit-mode"
                onClick={() => setIsEditModeEnabled(true)}
              />
            )}
          </div>
        )}

        <div
          className={cx(
            cm.knowledgeEditor__content,
            source === 'playground' && cm.knowledgeEditor__contentWithoutPadding,
          )}
        >
          {isReadMode && <ReadMode entry={selectedEntry} />}

          {isEditModeEnabled &&
            entryType === KnowledgeEntryType.TEXT &&
            (!selectedEntry || selectedEntry.type === KnowledgeEntryType.TEXT) && (
              <TextEntryEditor
                entry={selectedEntry}
                onSubmit={save}
                isLoading={isLoading}
                onBackToReadMode={handleBackToReadMode}
              />
            )}

          {isEditModeEnabled &&
            entryType === KnowledgeEntryType.LINK &&
            (!selectedEntry || selectedEntry.type === KnowledgeEntryType.LINK) && (
              <LinkWithPreviewEditor
                entry={selectedEntry as LinkEntry | null}
                onSubmit={save}
                isLoading={isLoading}
                onBackToReadMode={handleBackToReadMode}
              />
            )}
        </div>
      </section>
    </KnowledgeEditorContext.Provider>
  )
}
